import { useState } from 'react';

import { copy } from '../../core/copy';
import { PRESETS, encodeLook, sameIzgled, type Izgled } from '../../core/look';
import { Btn, HeroPortrait, Win } from '../components';
import { sfx } from '../audio';
import { availablePathsOf, onboard, useStore } from '../store';
import { IzgledEditor } from './Izgled';

/**
 * One screen, one real question: which program are you in.
 *
 * The character picker under it adds no concept to learn and takes one tap, and being able to point
 * at the little person on the map and say "that is me" is most of what makes a map worth walking
 * on. Twelve ready-made people, not nine sliders — GDD §4 allows this screen exactly one real
 * question and the wardrobe is emphatically not it.
 *
 * `PRILAGODI` is the one concession, and it is deliberately a door rather than a section: closed by
 * default, no badge, nothing about it implying anybody should. The people who want to spend ten
 * minutes on their character will find it in three seconds, and everybody else never sees it.
 */
export function Onboarding() {
  const busy = useStore((s) => s.busy);
  const paths = useStore(availablePathsOf);
  const active = paths.filter((p) => p.active);
  const inactive = paths.filter((p) => !p.active);

  const [path, setPath] = useState<string>(active[0]?.id ?? 'adobe');
  const [izgled, setIzgled] = useState<Izgled>(PRESETS[0]!);
  const [tweaking, setTweaking] = useState(false);

  return (
    <div className="centered">
      <img src="/brand/talas-white.svg" alt="Talas" className="logo-mark" style={{ width: 'min(180px, 46vw)' }} />
      <h1 className="title" style={{ color: '#f5fafc', fontSize: 20, textAlign: 'center' }}>
        {copy.onboarding.question}
      </h1>

      <div className="pathgrid">
        {active.map((option) => (
          <button
            key={option.id}
            className={`pathcard ${path === option.id ? 'on' : ''}`.trim()}
            onClick={() => {
              sfx.move();
              setPath(option.id);
            }}
            aria-pressed={path === option.id}
            aria-label={`${option.name}. ${option.description}`}
          >
            <div className="nm">{option.name}</div>
            <div className="ds">{option.description}</div>
          </button>
        ))}

        {inactive.map((option) => (
          <p key={option.id} className="tiny" style={{ color: '#9dc0d1', textAlign: 'center' }}>
            {option.name} — {option.note ?? copy.onboarding.soon}
          </p>
        ))}
      </div>

      <div style={{ textAlign: 'center', width: '100%', maxWidth: 420 }}>
        <p className="lbl" style={{ color: '#9dc0d1' }}>
          {copy.onboarding.lookQuestion}
        </p>
        <div className="lookrow">
          {PRESETS.map((preset, index) => (
            <button
              key={index}
              className={`lookbtn ${sameIzgled(preset, izgled) ? 'on' : ''}`.trim()}
              onClick={() => {
                sfx.move();
                setIzgled(preset);
              }}
              aria-label={`Izgled ${index + 1}`}
              aria-pressed={sameIzgled(preset, izgled)}
            >
              <HeroPortrait look={preset} scale={2} />
            </button>
          ))}
        </div>

        {tweaking ? (
          <div className="izwrap dark">
            <IzgledEditor value={izgled} onChange={setIzgled} />
          </div>
        ) : (
          <button
            className="btn ghost small"
            style={{ marginTop: 14 }}
            onClick={() => {
              sfx.move();
              setTweaking(true);
            }}
          >
            {copy.izgled.tweak}
          </button>
        )}
      </div>

      <Btn variant="primary" disabled={busy} onClick={() => void onboard(path, encodeLook(izgled))}>
        {busy ? '…' : copy.onboarding.start}
      </Btn>

      <p className="tiny" style={{ color: '#9dc0d1', textAlign: 'center', maxWidth: 380 }}>
        {copy.onboarding.hint}
      </p>
    </div>
  );
}

export { Win };
